import {useCallback, useLayoutEffect, useRef, useState} from 'react';

const VIEWPORT_MARGIN = 8;

/**
 * Positionne un élément flottant en `fixed` sous (ou au-dessus de) son ancre,
 * pour échapper aux conteneurs qui rognent leur contenu. À combiner avec un
 * portail vers `document.body`.
 *
 * @param {boolean} isOpen - le flottant est-il affiché
 * @param {{anchorInline?: 'left'|'right'}} options - bord de l'ancre sur lequel aligner le flottant
 * @returns {{anchorRef: React.RefObject, floatingRef: React.RefObject, floatingStyle: Object|undefined}}
 */
export default function useFixedPosition(isOpen, {anchorInline = 'left'} = {}) {
    const anchorRef = useRef(null);
    const floatingRef = useRef(null);
    const [floatingStyle, setFloatingStyle] = useState(null);

    const updatePosition = useCallback(() => {
        if (!anchorRef.current || !floatingRef.current) return;

        const anchor = anchorRef.current.getBoundingClientRect();
        const {clientWidth: viewportWidth, clientHeight: viewportHeight} = document.documentElement;
        const spaceBelow = viewportHeight - anchor.bottom;
        const spaceAbove = anchor.top;
        const hasRoomBelow = floatingRef.current.scrollHeight <= spaceBelow || spaceBelow >= spaceAbove;

        setFloatingStyle({
            position: 'fixed',
            maxHeight: (hasRoomBelow ? spaceBelow : spaceAbove) - VIEWPORT_MARGIN,
            overflowY: 'auto',
            ...(anchorInline === 'right'
                ? {right: viewportWidth - anchor.right}
                : {left: anchor.left}),
            ...(hasRoomBelow
                ? {top: anchor.bottom}
                : {bottom: viewportHeight - anchor.top}),
        });
    }, [anchorInline]);

    useLayoutEffect(() => {
        if (!isOpen) {
            setFloatingStyle(null);
            return;
        }

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    return {anchorRef, floatingRef, floatingStyle: floatingStyle ?? undefined};
}
